// Command search (cmdk): navigation and recent creatives. Opens with ⌘K / Ctrl+K.
import { useCompany } from "@/features/commercial/queries";
import { commercialNav } from "@/features/commercial/labels";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { Palette, Plus } from "lucide-react";
import { useFrontDesk } from "@/api/hooks";
import { Dialog, DialogContent } from "@/ui/dialog";
import { navigation } from "./nav";

export function CommandMenu({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const navigate = useNavigate();
  const { data: fd } = useFrontDesk(orgId);
  const { data: company } = useCompany(orgId);
  const go = (to: string) => {
    onOpenChange(false);
    void navigate({ to });
  };
  const items = navigation(orgId, fd, commercialNav(company)).flatMap((g) => g.items);
  const option =
    "flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-sunken [&_svg]:size-4 [&_svg]:text-muted";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Buscar o ir a…" className="max-w-xl gap-2 p-2 pt-4">
        <Command label="Buscar o ir a" className="grid gap-2">
          <Command.Input
            placeholder="Escribe para buscar…"
            className="h-9 rounded-md border border-line-strong bg-surface px-2.5 text-sm"
          />
          <Command.List className="max-h-80 overflow-y-auto">
            <Command.Empty className="px-2 py-6 text-center text-sm text-muted">Sin resultados.</Command.Empty>
            <Command.Group
              heading="Ir a"
              className="text-xs text-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
            >
              {fd?.capabilities.createCreative ? (
                <Command.Item
                  className={option}
                  onSelect={() => {
                    go(`/o/${orgId}/create`);
                  }}
                >
                  <Plus /> Crear creativo
                </Command.Item>
              ) : null}
              {items.map((item) => (
                <Command.Item
                  key={item.to}
                  className={option}
                  onSelect={() => {
                    go(item.to);
                  }}
                >
                  <item.icon /> {item.label}
                </Command.Item>
              ))}
            </Command.Group>
            {fd && fd.recentCreatives.length > 0 ? (
              <Command.Group
                heading="Creativos recientes"
                className="text-xs text-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1"
              >
                {fd.recentCreatives.map((c) => (
                  <Command.Item
                    key={c.id}
                    className={option}
                    value={`${c.name} ${c.id}`}
                    onSelect={() => {
                      go(`/o/${orgId}/creatives/${c.id}`);
                    }}
                  >
                    <Palette /> {c.name}
                  </Command.Item>
                ))}
              </Command.Group>
            ) : null}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
